import { ApplicationTypes } from './applications';
import { REDIRECT_TO_QUERY } from './authorization';

type QueryValue = boolean | number | string | null | undefined;

const withQuery = (pathname: string, query?: Record<string, QueryValue>) => {
	const searchParams = new URLSearchParams();

	Object.entries(query ?? {}).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			searchParams.set(key, String(value));
		}
	});

	const search = searchParams.toString();
	return search ? `${pathname}?${search}` : pathname;
};

export const MAIN_URL = '/';
export const LOGIN_URL = '/login';
export const REGISTRATION_URL = '/registration';
export const PASSWORD_RECOVERY_URL = '/password-recovery';
export const REGISTER_STORAGE_URL = '/register-storage';
export const STORAGE_SEARCH_URL = '/poisk-rybnogo-sklada';
export const HELP_CENTER_URL = '/spravochniy-centr';
export const LANDING_ADMIN_URL = '/landing-admin';
export const DIGITAL_OFFICE_URL = '/digital-office';
export const NOT_FOUND_URL = '/404';
export const MANIFEST_URL = '/manifest.json';
export const FAVICON_URL = '/favicon.ico';

export const PERSONAL_ACCOUNT_URL = '/personal-account';
export const PERSONAL_ACCOUNT_APPLICATIONS_URL = `${PERSONAL_ACCOUNT_URL}/applications`;
export const PERSONAL_ACCOUNT_PROFILE_URL = `${PERSONAL_ACCOUNT_URL}/profile`;
export const PERSONAL_ACCOUNT_ANALYTICS_URL = `${PERSONAL_ACCOUNT_URL}/analytics`;
export const PERSONAL_ACCOUNT_COUNTERPARTIES_URL = `${PERSONAL_ACCOUNT_URL}/counterparties`;
export const PERSONAL_ACCOUNT_FISH_PRODUCTS_URL = `${PERSONAL_ACCOUNT_URL}/fish-products`;
export const PERSONAL_ACCOUNT_PAYMENTS_URL = `${PERSONAL_ACCOUNT_URL}/payments`;
export const PERSONAL_ACCOUNT_SETTINGS_URL = `${PERSONAL_ACCOUNT_URL}/settings`;
export const PERSONAL_ACCOUNT_SETTINGS_INVITE_EMPLOYEE_URL = `${PERSONAL_ACCOUNT_SETTINGS_URL}/invite-employee`;
export const PERSONAL_ACCOUNT_WAREHOUSES_URL = `${PERSONAL_ACCOUNT_URL}/warehouses`;
export const EXCHANGE_URL = `${PERSONAL_ACCOUNT_URL}/exchange`;

export const STORAGE_ACCOUNT_URL = '/storage-account';
export const STORAGE_ACCOUNT_APPLICATIONS_URL = `${STORAGE_ACCOUNT_URL}/applications`;
export const STORAGE_ACCOUNT_REFRIGERATORS_URL = `${STORAGE_ACCOUNT_URL}/refrigerators`;
export const STORAGE_ACCOUNT_SUPPLIERS_URL = `${STORAGE_ACCOUNT_URL}/suppliers`;
export const STORAGE_ACCOUNT_FISH_PRODUCTS_URL = `${STORAGE_ACCOUNT_URL}/fish-products`;
export const STORAGE_ACCOUNT_ADDITIONAL_SERVICES_URL = `${STORAGE_ACCOUNT_URL}/additional-services`;
export const STORAGE_ACCOUNT_SETTINGS_URL = `${STORAGE_ACCOUNT_URL}/settings`;

export const APPLICATIONS_URL = '/applications';
export const APPLICATIONS_VIEW_URL = '/applications/view';
export const ADD_PRODUCTS_APPLICATION_URL = `${APPLICATIONS_URL}/add-products`;
export const STORAGE_APPLICATION_URL = `${APPLICATIONS_URL}/storage-application`;
export const TRANSFER_APPLICATION_URL = `${APPLICATIONS_URL}/transfer-application`;
export const SHIPMENT_APPLICATION_URL = `${APPLICATIONS_URL}/shipment-application`;
export const PURCHASE_APPLICATION_URL = `${APPLICATIONS_URL}/purchase-application`;
export const UNREGISTERED_PURCHASE_APPLICATION_URL = `${APPLICATIONS_URL}/unregistred-purchase-application`;
export const AGREEMENT_APPLICATION_URL = `${APPLICATIONS_URL}/agreement-application`;
export const SERVICE_APPLICATION_URL = `${APPLICATIONS_URL}/service-application`;

export const JOURNAL_URL = '/journal';
export const JOURNAL_SEARCH_URL = `${JOURNAL_URL}/search`;
export const JOURNAL_MAIN_SECTION_URL = `${JOURNAL_URL}/section/main`;
export const OFFERS_ANCHOR_URL = '#offers';
export const TOP_ANCHOR_URL = '#';
export const KATALOG_URL = '/katalog';
export const POSTAVSCHIKI_RYBY_URL = '/postavschiki-ryby';
export const O_KOMPANII_URL = '/o-kompanii';
export const CALCULATOR_URL = '/calculator';

export const AVTOMATIZACIYA_KOMMUNIKACII_S_KLIENTAMI_URL =
	'/avtomatizaciya-kommunikacii-s-klientami';
export const PRESS_RELEASE_URL = '/zakluchenie-soglasheniya-s-vostochnoy-birzhey';

export const MAKE_MAIN_URL = (anchor?: string) => (anchor ? `${MAIN_URL}#${anchor}` : MAIN_URL);
export const MAKE_ANCHOR_URL = (anchor: string) => `#${anchor}`;

export const MAKE_SPRAVOCHNIY_CENTR_LINK = (anchor?: string) =>
	anchor ? `${HELP_CENTER_URL}#${anchor}` : HELP_CENTER_URL;

export const MAKE_LOGIN_URL = (params?: { error?: string; redirectTo?: string }) =>
	withQuery(LOGIN_URL, { error: params?.error, [REDIRECT_TO_QUERY]: params?.redirectTo });

export const MAKE_REGISTRATION_URL = (params?: { redirectTo?: string }) =>
	withQuery(REGISTRATION_URL, { [REDIRECT_TO_QUERY]: params?.redirectTo });

export const MAKE_APPLICATION_VIEW_URL = (id: number | string) => `${APPLICATIONS_URL}/view/${id}`;

export const MAKE_APPLICATION_URL = (applicationTypeUrl: string, id?: number | string) =>
	`${APPLICATIONS_URL}/${applicationTypeUrl}${id ? `/${id}` : ''}`;

export const MAKE_ADD_PRODUCTS_APPLICATION_URL = (params?: {
	id?: number | string;
	warehouseId?: number | string;
}) =>
	withQuery(`${ADD_PRODUCTS_APPLICATION_URL}${params?.id ? `/${params.id}` : ''}`, {
		warehouseId: params?.warehouseId,
	});

export const MAKE_STORAGE_APPLICATION_URL = (
	id?: number | string,
	params?: { name?: string; warehouseId?: number | string },
) => withQuery(`${STORAGE_APPLICATION_URL}${id ? `/${id}` : ''}`, params);

export const MAKE_TRANSFER_APPLICATION_URL = (
	id?: number | string,
	params?: { name?: string; warehouseId?: number | string },
) => withQuery(`${TRANSFER_APPLICATION_URL}${id ? `/${id}` : ''}`, params);

export const MAKE_SHIPMENT_APPLICATION_URL = (
	id?: number | string,
	params?: { name?: string; warehouseId?: number | string },
) => withQuery(`${SHIPMENT_APPLICATION_URL}${id ? `/${id}` : ''}`, params);

export const MAKE_AGREEMENT_APPLICATION_URL = (params?: { warehouseId?: number | string }) =>
	withQuery(AGREEMENT_APPLICATION_URL, params);

export const MAKE_SERVICE_APPLICATION_URL = (params?: {
	name?: string;
	warehouseId?: number | string;
}) => withQuery(SERVICE_APPLICATION_URL, params);

export const MAKE_PURCHASE_APPLICATION_URL = (params?: Record<string, QueryValue>) =>
	withQuery(PURCHASE_APPLICATION_URL, params);

export const MAKE_UNREGISTERED_PURCHASE_APPLICATION_URL = (params?: Record<string, QueryValue>) =>
	withQuery(UNREGISTERED_PURCHASE_APPLICATION_URL, params);

export const MAKE_APPLICATION_BY_TYPE_LINK = (
	type: ApplicationTypes,
	params?: { name?: string },
) => {
	const routes: Partial<Record<ApplicationTypes, string>> = {
		[ApplicationTypes.Storage]: STORAGE_APPLICATION_URL,
		[ApplicationTypes.Transfer]: TRANSFER_APPLICATION_URL,
		[ApplicationTypes.Shipment]: SHIPMENT_APPLICATION_URL,
		[ApplicationTypes.AdditionalServices]: SERVICE_APPLICATION_URL,
	};

	return withQuery(routes[type] ?? APPLICATIONS_URL, params);
};

export const MAKE_PERSONAL_ACCOUNT_FISH_PRODUCT_URL = (id: number | string) =>
	`${PERSONAL_ACCOUNT_FISH_PRODUCTS_URL}/${id}`;

export const MAKE_PERSONAL_ACCOUNT_COUNTERPARTY_URL = (id: number | string) =>
	`${PERSONAL_ACCOUNT_COUNTERPARTIES_URL}/${id}`;

export const MAKE_PERSONAL_ACCOUNT_SETTINGS_EMPLOYEE_URL = (id: number | string) =>
	`${PERSONAL_ACCOUNT_SETTINGS_URL}/employee/${id}`;

export const MAKE_PERSONAL_ACCOUNT_WAREHOUSE_URL = (id: number | string) =>
	`${PERSONAL_ACCOUNT_WAREHOUSES_URL}/${id}`;

export const MAKE_PERSONAL_ACCOUNT_WAREHOUSES_URL = (params?: { tab?: string }) =>
	withQuery(STORAGE_SEARCH_URL, params);

export const MAKE_PERSONAL_ACCOUNT_PAYMENTS_URL = (params?: { tariffModal?: boolean }) =>
	withQuery(PERSONAL_ACCOUNT_PAYMENTS_URL, {
		'tariff-modal': params?.tariffModal,
	});

export const MAKE_STORAGE_ACCOUNT_APPLICATIONS_URL = (params?: { status?: string }) =>
	withQuery(STORAGE_ACCOUNT_APPLICATIONS_URL, params);

export const MAKE_STORAGE_ACCOUNT_REFRIGERATOR_URL = (id: number | string) =>
	`${STORAGE_ACCOUNT_REFRIGERATORS_URL}/${id}`;

export const MAKE_STORAGE_ACCOUNT_SUPPLIER_URL = (id: number | string) =>
	`${STORAGE_ACCOUNT_SUPPLIERS_URL}/${id}`;

export const MAKE_POSTAVSCHIKI_RYBY_URL = (
	alias?: string,
	params?: { anchor?: string; productId?: number | string },
) => {
	const pathname = alias ? `${POSTAVSCHIKI_RYBY_URL}/${alias}` : POSTAVSCHIKI_RYBY_URL;
	const url = withQuery(pathname, { productId: params?.productId });
	return params?.anchor ? `${url}#${params.anchor}` : url;
};

export const MAKE_KATALOG_URL = (params?: { productId?: number | string }) =>
	withQuery(KATALOG_URL, params);

export const MAKE_JOURNAL_URL = ({ section, slug }: { section?: string; slug?: string } = {}) => {
	if (section) return `${JOURNAL_URL}/section/${section}`;
	if (slug) return `${JOURNAL_URL}/${slug}`;
	return JOURNAL_URL;
};

export const MAKE_JOURNAL_PREVIEW_URL = (slug: string) => `${JOURNAL_URL}/preview/${slug}`;

export const MAKE_JOURNAL_SEARCH_URL = (params?: { query?: string; tag?: string }) =>
	withQuery(JOURNAL_SEARCH_URL, { q: params?.query, t: params?.tag });

export const MAKE_EXCHANGE_REPORT_URL = ({
	fromApplication,
	targetApplication,
	contextId,
}: {
	fromApplication?: string;
	targetApplication?: string;
	contextId?: string;
} = {}) =>
	withQuery(`${EXCHANGE_URL}/report${targetApplication ? `/${targetApplication}` : ''}`, {
		fromApplication,
		contextId,
	});

export const MAKE_EXCHANGE_REPORT_VIEW_URL = ({
	targetApplication,
	rowType,
}: {
	targetApplication?: string;
	rowType?: string;
} = {}) =>
	withQuery(`${EXCHANGE_URL}/report-view${targetApplication ? `/${targetApplication}` : ''}`, {
		rowType,
	});

export const PRODUCTION_MAIN_URL = 'https://www.fishplace.ru';
export const DEMO_LOGIN_URL = 'https://demo.fishplace.su/login';
export const OFFICE_LANDING_URL = 'https://office.fishplace.ru/';
export const SPBEX_URL = 'https://www.spbex.ru/';
export const TG_CHANNEL_FISHPLACE_URL = 'https://t.me/fishplaceru';
export const TG_SALES_BOT_URL = 'https://t.me/FishplaceSalesBot';
export const TG_FIND_PROVIDER_POST_URL = `${TG_CHANNEL_FISHPLACE_URL}/264`;
export const WHATSAPP_FISHPLACE_URL = 'https://api.whatsapp.com/send?phone=79089998080';
export const RUTUBE_FISHPLACE_URL = 'https://rutube.ru/channel/40937493/';
export const YANDEX_DEMO_REQUEST_FORM_URL =
	'https://forms.yandex.ru/cloud/66f0c90173cee79c0dff63dd';
export const ARPP_URL = 'https://arpp.pk.ru/';
export const SK_URL = 'https://sk.ru';
export const MINCIFRY_PRIKAZ_URL =
	'https://publication.pravo.gov.ru/document/0001202308150009?index=1';

export const FISHPLACE_PHONE_URL = 'tel:+79089998080';
export const FISHPLACE_INFO_EMAIL_URL = 'mailto:info@fishplace.ru';
export const FISHPLACE_SUPPORT_EMAIL_URL = 'mailto:hello@fishplace.ru';

export const MAKE_PHONE_URL = (phone: string) => `tel:${phone}`;
export const MAKE_EMAIL_URL = (email: string, subject?: string) =>
	`mailto:${email}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`;

export const AUTHORIZATION_REQUIRED_PATHS = [
	PERSONAL_ACCOUNT_URL,
	STORAGE_ACCOUNT_URL,
	LANDING_ADMIN_URL,
	ADD_PRODUCTS_APPLICATION_URL,
	STORAGE_APPLICATION_URL,
	TRANSFER_APPLICATION_URL,
	SHIPMENT_APPLICATION_URL,
	PURCHASE_APPLICATION_URL,
	AGREEMENT_APPLICATION_URL,
	SERVICE_APPLICATION_URL,
	APPLICATIONS_VIEW_URL,
];
